import {BindingViewState} from '@mintdata-wsp/common/state/ui/visualDesigner/BindingState';
import {BindingPhase} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/BindingPhase';
import {MeshLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshLocation';
import {MeshState} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshState';
import {MeshViewTargetType} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshViewTargetType';
import {AnySlot} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {SlotLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SlotLocation';
import {MeshUtils} from '@mintdata-wsp/common/util/MeshUtils';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {Resizer} from '@mintdata-wsp/wsp/ui/components/resizable/Resizer';
import {area, mk} from '@mintdata/mint-kit/api/log/mk';
import {DomUtils} from '@mintdata/mint-kit/impl/util/DomUtils';
import {action} from 'mobx';
import * as React from 'react';

interface ITargetOffset
{
    x:number;
    y:number;
    width:number;
    height:number;
}

interface IMeshOnlyGridBounds
{
    left:number
    top:number;
    right:number;
    bottom:number;
}

interface IFormulaCellRefSelectionDeltas
{
    deltaCol:number;
    deltaRow:number;
}

export class MeshMouseController
{
    private readonly _defaultCornerWidth:number = 48;  // TODO: ???
    private readonly _defaultCornerHeight:number = 32; // TODO: ???

    private _getTargetOffsetFromRoot(ctx:IWSPMeshContext, event:React.MouseEvent<HTMLElement>):ITargetOffset
    {
        const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);
        const {alignedScrollLeft, alignedScrollTop} = meshState;

        const rootRect:ClientRect = event.currentTarget.getBoundingClientRect();

        return {
            x: event.clientX - rootRect.left + alignedScrollLeft,
            y: event.clientY - rootRect.top + alignedScrollTop,
            width: rootRect.width - 10, // Scroll size
            height: rootRect.height - 10 // Scroll size
        };
    }

    private _isNotInContentArea(ctx:IWSPMeshContext, targetOffset:ITargetOffset):boolean
    {
        const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);
        const {scrollLeft, scrollTop} = meshState.details;

        return targetOffset.x - scrollLeft > targetOffset.width
            || targetOffset.y - scrollTop > targetOffset.height;
    }

    private _isResizer(target:HTMLElement):boolean
    {
        let isResizer:boolean = false;

        DomUtils.eachParentElement(target, (elementRef:HTMLElement) =>
        {
            const dataType:string = elementRef.getAttribute('data-type');

            isResizer = isResizer || dataType === Resizer.ElementDataType;

            return !isResizer;
        });

        return isResizer;
    }

    private _getMeshOnlyGridBounds(meshElement:HTMLElement):IMeshOnlyGridBounds|null
    {
        if (!meshElement)
            return  null;

        const rootRect:ClientRect = meshElement.getBoundingClientRect();

       return {
            left: rootRect.left + this._defaultCornerWidth,
            top: rootRect.top + this._defaultCornerHeight,
            right: rootRect.right - 10, // Scroll size
            bottom: rootRect.bottom - 10 // Scroll size
        };
    }

    private _getFormulaCellRefSelectionDeltas(location:MeshLocation,
                                              selectCellRefStartLocation:MeshLocation,
                                              inputColIndex:number,
                                              inputRowIndex:number):IFormulaCellRefSelectionDeltas
    {
        return {
            deltaCol: location.colIndex != null && selectCellRefStartLocation.colIndex != null
                ? location.colIndex - inputColIndex
                : (selectCellRefStartLocation.colIndex != null ? -inputColIndex : null),
            deltaRow: location.rowIndex != null && selectCellRefStartLocation.rowIndex != null
                ? location.rowIndex - inputRowIndex
                : (selectCellRefStartLocation.rowIndex != null ? -inputRowIndex : null)
        };
    }

    @action
    public onMouseDown(ctx:IWSPMeshContext, event:React.MouseEvent<HTMLElement>):void
    {
        const {spreadsheets: {main, mesh, meshInput, meshSelection}, bindings} = controllers.ui;

        const {editSheetId, input: inputState} = main.getEditorState();

        if( this._isResizer(event.target as HTMLElement) )
            return;

        const meshState = mesh.getState(ctx);
        const targetOffset:ITargetOffset = this._getTargetOffsetFromRoot(ctx, event);

        if( this._isNotInContentArea(ctx, targetOffset) )
            return;

        const cellAtCursor:MeshLocation = MeshUtils.getCellAtCursor(
            targetOffset.x,
            targetOffset.y,
            meshState,
            this._defaultCornerWidth,
            this._defaultCornerHeight
        );

        if( cellAtCursor )
        {
            meshSelection.setCellAtCursor(ctx, cellAtCursor);

            //if right button is clicked...
            if( event.button === 2 )
            {
                if( bindings.isBindingInProgress() )
                    bindings.stopBinding();
                else if( main.isEditMode )
                    mesh.commitEditingCellIfValid(ctx);

                const {details: {selectedRange: {from, to}}, size: {numCols, numRows}} = meshState;

                const fromCol = from.colIndex !== null ? from.colIndex : 0;
                const fromRow = from.rowIndex !== null ? from.rowIndex : 0;
                const toCol = to.colIndex !== null ? to.colIndex : numCols - 1;
                const toRow = to.rowIndex !== null ? to.rowIndex : numRows - 1;

                if( (cellAtCursor.colIndex || 0) < fromCol || cellAtCursor.colIndex > toCol ||
                    (cellAtCursor.rowIndex || 0) < fromRow || cellAtCursor.rowIndex > toRow )
                {
                    meshSelection.selectCell(ctx, cellAtCursor);
                }
            }
            else if( bindings.isBindingInProgress() )
            {
                bindings.handleMeshMouseDown(ctx, cellAtCursor, event);
            }
            else if( main.isEditMode && inputState.isSelectCellRefMode )
            {
                inputState.isSelectCellRefRangeMode = true;
                inputState.selectCellRefStartLocation = cellAtCursor;

                if( editSheetId !== ctx.sheetId )
                {
                    meshInput.selectNewExternalCellRef(
                        ctx,
                        cellAtCursor.colIndex,
                        cellAtCursor.rowIndex,
                        false
                    );
                }
                else
                    meshInput.selectNewCellRef(
                        ctx,
                        cellAtCursor.colIndex != null ? cellAtCursor.colIndex - inputState.colIndex : null,
                        cellAtCursor.rowIndex != null ? cellAtCursor.rowIndex - inputState.rowIndex : null,
                        false
                    );
            }
            else
            {
                if( main.isEditMode )
                    mesh.commitEditingCellIfValid(ctx);
                else if( meshState.details.isEditNoteMode )
                    mesh.finishEditingNote(ctx);

                if( event.shiftKey )
                {
                    let startSelectionCell:MeshLocation = meshState.details.activeCell;

                    if( cellAtCursor.colIndex === null )
                    {
                        startSelectionCell = new MeshLocation(null, meshState.details.activeCell.rowIndex);
                    }
                    else if( cellAtCursor.rowIndex === null )
                    {
                        startSelectionCell = new MeshLocation(meshState.details.activeCell.colIndex, null);
                    }

                    meshSelection.startRangeSelection(ctx, startSelectionCell);
                    meshSelection.setSelectionRangeTo(ctx, cellAtCursor);
                }
                else
                {
                    let cellToSelect:MeshLocation = cellAtCursor;

                    if( cellAtCursor.colIndex === null )
                    {
                        cellToSelect = new MeshLocation(meshState.visibleStartColIndex, cellAtCursor.rowIndex);
                    }
                    else if( cellAtCursor.rowIndex === null )
                    {
                        cellToSelect = new MeshLocation(cellAtCursor.colIndex, meshState.visibleStartRowIndex);
                    }

                    meshSelection.selectCell(ctx, cellToSelect);
                    meshSelection.startRangeSelection(ctx, cellAtCursor);
                }
            }
        }
        else
        {
            const cellToSelect = new MeshLocation(meshState.visibleStartColIndex, meshState.visibleStartRowIndex);

            meshSelection.setCellAtCursor(ctx, cellToSelect);
            meshSelection.selectCell(ctx, cellToSelect);

            meshSelection.startRangeSelection(ctx, new MeshLocation(0, 0));
            meshSelection.setSelectionRangeTo(ctx, new MeshLocation(
                meshState.colWidths.length - 1,
                meshState.rowHeights.length - 1
            ));
        }
    }

    @action
    public onMouseMove(ctx:IWSPMeshContext, event:React.MouseEvent<HTMLElement>):void
    {
        const {spreadsheets: {main, mesh, meshInput, meshSelection}, bindings} = controllers.ui;
        const meshState = mesh.getState(ctx);
        const viewState = bindings.getViewState();

        const {editSheetId, input: inputState} = main.getEditorState();

        const targetOffset:ITargetOffset = this._getTargetOffsetFromRoot(ctx, event);
        const boundsOfOnlyMeshGrid = this._getMeshOnlyGridBounds(event.currentTarget);

        if ( boundsOfOnlyMeshGrid && (event.clientX < boundsOfOnlyMeshGrid.left || event.clientX > boundsOfOnlyMeshGrid.right
            || event.clientY < boundsOfOnlyMeshGrid.top || event.clientY > boundsOfOnlyMeshGrid.bottom)
        )
            this._tryToStartScrollToExtendSelectOnMesh(ctx, event);
        else
            this.stopTrackingMouseOutsideMesh(ctx);

        if( this._isNotInContentArea(ctx, targetOffset) )
        {
            viewState.activeSheetId = null;
            return;
        }

        viewState.activeSheetId = ctx.sheetId;

        const targetType:string = document.activeElement
            ? document.activeElement.getAttribute('data-type')
            : null;

        if( bindings.isBindingInProgress() && targetType !== MeshViewTargetType.Table )
            mesh.focusMesh(ctx);

        const cellAtCursor:MeshLocation = MeshUtils.getCellAtCursor(
            targetOffset.x,
            targetOffset.y,
            meshState,
            this._defaultCornerWidth,
            this._defaultCornerHeight
        );

        if( cellAtCursor )
        {
            meshSelection.setCellAtCursor(ctx, cellAtCursor);

            // rowHeader or columnHeader
            if( cellAtCursor.colIndex === null || cellAtCursor.rowIndex === null )
            {
                if( meshState.details.isSelectRangeMode
                    && (meshState.details.selectionRangeFrom.colIndex === null || meshState.details.selectionRangeFrom.rowIndex === null)
                )
                    meshSelection.setSelectionRangeTo(ctx, cellAtCursor);

            }
            else if( bindings.isBindingInProgress() )
            {
                bindings.handleMeshMouseMove(ctx, cellAtCursor, event);
            }
            else if( main.isEditMode && inputState.isSelectCellRefMode && inputState.isSelectCellRefRangeMode )
            {
                if( !cellAtCursor.isEqualTo(inputState.selectCellRefStartLocation) )
                {
                    const deltas:IFormulaCellRefSelectionDeltas = this._getFormulaCellRefSelectionDeltas(
                        cellAtCursor,
                        inputState.selectCellRefStartLocation,
                        inputState.colIndex,
                        inputState.rowIndex
                    );

                    if( editSheetId !== ctx.sheetId )
                    {
                        meshInput.selectNewExternalCellRefRange(
                            ctx,
                            cellAtCursor.colIndex,
                            cellAtCursor.rowIndex,
                            false
                        );
                    }
                    else
                        meshInput.selectNewCellRefRange(
                            ctx,
                            deltas.deltaCol,
                            deltas.deltaRow,
                            false
                        );
                }
            }
            else if( meshState.details.isSelectRangeMode )
                meshSelection.setSelectionRangeTo(ctx, cellAtCursor);
            else if( meshState.details.isExtendRangeMode )
                meshSelection.setExtendRangeTo(ctx, cellAtCursor);
        }
        else
        {
            meshSelection.setCellAtCursor(ctx, null);
        }
    }

    @action
    public onMouseUp(ctx:IWSPMeshContext, event:React.MouseEvent<HTMLElement>):void
    {
        const {spreadsheets: {main, mesh, meshInput, meshSelection}, bindings} = controllers.ui;
        const meshState = mesh.getState(ctx);

        const inputState = main.getEditorState().input;

        const targetOffset:ITargetOffset = this._getTargetOffsetFromRoot(ctx, event);

        if( this._isNotInContentArea(ctx, targetOffset) )
            return;

        mk.debug(area.Mesh, `MeshMouseController :: onMouseUp, ${targetOffset.x}, ${targetOffset.y}`, event);

        const cellAtCursor:MeshLocation = MeshUtils.getCellAtCursor(
            targetOffset.x,
            targetOffset.y,
            meshState,
            this._defaultCornerWidth,
            this._defaultCornerHeight
        );

        if( cellAtCursor && bindings.isBindingInProgress() )
        {
            bindings.handleMeshMouseUp(ctx, cellAtCursor, event);
        }
        else if( main.isEditMode )
        {
            inputState.isSelectCellRefRangeMode = false;
            inputState.selectCellRefStartLocation = null;

            meshInput.focusInputControl();
        }
        else
        {
            meshSelection.finishRangeExtension(ctx, event.altKey);
            meshSelection.finishRangeSelection(ctx);
            mesh.focusMesh(ctx);
        }
    }

    @action
    public onMouseDoubleClick(ctx:IWSPMeshContext, event:React.MouseEvent<HTMLElement>):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);
        const {sheetId, sheetCtrl} = ctx;

        const targetOffset:ITargetOffset = this._getTargetOffsetFromRoot(ctx, event);

        mk.debug(area.Mesh, `MeshMouseController :: onMouseDoubleClick, ${targetOffset.x}, ${targetOffset.y}`, event);

        if( this._isNotInContentArea(ctx, targetOffset) )
        {
            return;
        }

        const cellAtCursor = MeshUtils.getCellAtCursor(
            targetOffset.x,
            targetOffset.y,
            meshState,
            this._defaultCornerWidth,
            this._defaultCornerHeight
        );

        if( cellAtCursor )
        {
            mk.debug(area.Mesh, `DOUBLE CLICK: in viewport`);

            const {colIndex, rowIndex} = cellAtCursor;
            const slot:AnySlot = sheetCtrl.findSlot(sheetId, new SlotLocation(colIndex, rowIndex));

            // Prevent editing a lazy cell or cell that contains a frame (instead edit pencil is drawn on the ui)
            if( (slot && (slot.isLazy || slot.containsFrame)) )
            {
                return;
            }

            mesh.startEditingCell(ctx);
        }
        else
            mk.debug(area.Mesh, `DOUBLE CLICK: ***NOT*** in viewport`);
    }

    @action
    public onMouseLeave(ctx:IWSPMeshContext, event:React.MouseEvent<HTMLElement>):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        // sometimes when move fast mouseMove event will not fired on places where scroll should start
        this._tryToStartScrollToExtendSelectOnMesh(ctx, event, 2);

        meshState.details.scrollWhenMouseOutsideSpeed = 2;
    }

    @action.bound
    public onMouseEnter(ctx:IWSPMeshContext, event:React.MouseEvent<HTMLElement>):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        meshState.details.scrollWhenMouseOutsideSpeed = 1;
    }

    @action
    public onSelectionFrameMouseDown(ctx:IWSPMeshContext, event:React.MouseEvent<HTMLElement>):void
    {
        const {meshSelection} = controllers.ui.spreadsheets;
        meshSelection.startRangeExtension(ctx);
    }

    @action
    public onDocumentMouseMove(event:MouseEvent):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const ctx:IWSPMeshContext | null = mesh.getContextOfFocusedSheet();
        if( !ctx )
            return;

        const meshState = mesh.getState(ctx);

        if( meshState.details.mouseOutsideMeshPosition )
        {
            meshState.details.mouseOutsideMeshPosition = {
                x: event.clientX,
                y: event.clientY,
            };
        }
    }

    @action.bound
    public onDocumentMouseUp(event:MouseEvent):void
    {
        const {spreadsheets: {main, mesh, meshSelection, meshInput}, bindings} = controllers.ui;

        const ctx:IWSPMeshContext | null = mesh.getContextOfFocusedSheet();
        const inputState = main.getEditorState().input;

        if( !ctx )
            return;

        const meshState:MeshState = mesh.getState(ctx);

        if( meshState.details.mouseOutsideMeshPosition )
        {
            if (bindings.isBindingInProgress())
            {
                bindings.handleDocumentMouseUp(ctx, event);
            }
            else if( main.isEditMode )
            {
                inputState.isSelectCellRefRangeMode = false;
                inputState.selectCellRefStartLocation = null;

                meshInput.focusInputControl();
            }
            else
            {
                meshSelection.finishRangeExtension(ctx, event.altKey);
                meshSelection.finishRangeSelection(ctx);
                mesh.focusMesh(ctx);
            }

            this.stopTrackingMouseOutsideMesh(ctx);
        }
    }

    @action
    public stopTrackingMouseOutsideMesh(ctx:IWSPMeshContext):void
    {
        if( !ctx )
            return;

        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        meshState.details.scrollWhenMouseOutsideSpeed = 0;
        meshState.details.mouseOutsideMeshPosition = null;
    }

    @action.bound
    private _tryToStartScrollToExtendSelectOnMesh(ctx:IWSPMeshContext,
                                                  event:React.MouseEvent<HTMLElement>,
                                                  scrollWhenMouseOutsideSpeed:number = 1):void
    {
        const {spreadsheets: {main, mesh, meshSelection, meshInput}, bindings} = controllers.ui;

        const meshState:MeshState = mesh.getState(ctx);

        const editorState = main.getEditorState();

        const inputState = editorState.input;

        if (meshState.details.scrollWhenMouseOutsideSpeed > 0)
            return;

        const viewState:BindingViewState = bindings.getViewState();
        const currentTarget = event.currentTarget;

        if (bindings.isBindingInProgress()
            || (main.isEditMode && inputState.isSelectCellRefMode && inputState.isSelectCellRefRangeMode)
            || meshState.details.isSelectRangeMode || meshState.details.isExtendRangeMode
        )
        {
            meshState.details.scrollWhenMouseOutsideSpeed = scrollWhenMouseOutsideSpeed;
            meshState.details.mouseOutsideMeshPosition = {
                x: event.clientX,
                y: event.clientY,
            };
        }

        const scrollFunction:() => void = () =>
        {
            if( meshState.details.scrollWhenMouseOutsideSpeed === 0 || !meshState.details.mouseOutsideMeshPosition )
                return;

            const {x, y} = meshState.details.mouseOutsideMeshPosition;
            const boundsOfOnlyMeshGrid = this._getMeshOnlyGridBounds(currentTarget);

            if( !boundsOfOnlyMeshGrid )
                return;

            const {
                visibleStartColIndex,
                visibleStartRowIndex,
                visibleEndColIndex,
                visibleEndRowIndex,
                size,
                colWidths,
                rowHeights,
            } = meshState;

            let colIndex:number, rowIndex:number;

            if( x < boundsOfOnlyMeshGrid.left )
            {
                colIndex = Math.max(visibleStartColIndex - 1, 0);
            }
            else if( x > boundsOfOnlyMeshGrid.right )
            {
                colIndex = Math.min(visibleEndColIndex + 1, size.numCols - 1);
            }
            else
            {
                const delta:number = x - boundsOfOnlyMeshGrid.left;
                let sum:number = 0;

                for( let i = visibleStartColIndex; i <= visibleEndColIndex; i++ )
                {
                    sum += colWidths[i];

                    if( sum >= delta || i === visibleEndColIndex)
                    {
                        colIndex = i;
                        break;
                    }
                }
            }

            if( y < boundsOfOnlyMeshGrid.top )
            {
                rowIndex = Math.max(visibleStartRowIndex - 1, 0);
            }
            else if( y > boundsOfOnlyMeshGrid.bottom )
            {
                rowIndex = Math.min(visibleEndRowIndex + 1, size.numRows - 1);
            }
            else
            {
                const delta:number = y - boundsOfOnlyMeshGrid.top;
                let sum:number = 0;

                for( let i = visibleStartRowIndex; i <= visibleEndRowIndex; i++ )
                {
                    sum += rowHeights[i];

                    if( sum >= delta || i === visibleEndRowIndex)
                    {
                        rowIndex = i;
                        break;
                    }
                }
            }

            const location:MeshLocation = new MeshLocation(colIndex, rowIndex);

            if( bindings.isBindingInProgress() )
            {
                bindings.handleMeshMouseScroll(ctx, location);
            }
            else if ((main.isEditMode && inputState.isSelectCellRefMode && inputState.isSelectCellRefRangeMode))
            {
                const deltas:IFormulaCellRefSelectionDeltas = this._getFormulaCellRefSelectionDeltas(
                    location,
                    inputState.selectCellRefStartLocation,
                    inputState.colIndex,
                    inputState.rowIndex
                );

                if( editorState.editSheetId !== ctx.sheetId )
                    meshInput.selectNewExternalCellRefRange(
                        ctx,
                        deltas.deltaCol !== null ? location.colIndex : null,
                        deltas.deltaRow !== null ? location.rowIndex : null,
                        false
                    );
                else
                    meshInput.selectNewCellRefRange(
                        ctx,
                        deltas.deltaCol,
                        deltas.deltaRow,
                        false
                    );
            }
            else if ( meshState.details.isExtendRangeMode )
            {
                meshSelection.extendExtendedRangeToCell(ctx, location);
            }
            else if ( meshState.details.isSelectRangeMode )
            {
                meshSelection.extendSelectionToCell(ctx, location);
            }

            setTimeout(scrollFunction, meshState.details.scrollWhenMouseOutsideSpeed === 1 ? 150 : 16);
        };

        setTimeout(scrollFunction, 16);
    }
}
